import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "step1", "step2",
    "title", "datePresets", "dateFrom", "dateTo",
    "responsible", "companiesInvolved",
    "progressOfWork", "expectedDone",
    "punchlistKind",
    "reportTypeSelect", "reportTypesList",
    "commentInput", "commentsList", "addCommentBtn",
    "photosGrid", "photosLoading", "loadMoreContainer",
    "selectAllPhotos", "photoCount",
    "photoFilterRange",
    "backBtn", "nextBtn", "exportBtn", "exportSpinner"
  ]

  static values = {
    exportUrl: String,
    photosUrl: String,
    generatingToast: String,
    readyToast: String
  }

  connect() {
    this._comments = []
    this._reportTypeIds = []
    this._selectedPhotoIds = new Set()
    this._currentPreset = "30"
    this._dateStart = null
    this._dateEnd = null
    this._currentStep = 1
    this._photosPage = 1
    this._photosLoaded = false
    this._isLastPhotoPage = false
    this._pollTimers = []
  }

  disconnect() {
    this._pollTimers.forEach(t => clearTimeout(t))
    this._pollTimers = []
  }

  // ── Modal ──────────────────────────────────────────────────────────

  open(event) {
    event.preventDefault()
    document.dispatchEvent(new CustomEvent("dropdown:close"))
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this._showStep(1)
    this._applyPresetDates("30")
  }

  hideModal(event) {
    if (event.target === this.modalTarget) this._close()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  close() {
    this._close()
  }

  _close() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  // ── Steps ──────────────────────────────────────────────────────────

  goNext(event) {
    event.preventDefault()
    if (!this._dateStart || !this._dateEnd) {
      this._showToast("Selecione o período.", "error")
      return
    }
    this._showStep(2)
    if (!this._photosLoaded) this._loadPhotos()
  }

  goBack(event) {
    event.preventDefault()
    this._showStep(1)
  }

  _showStep(step) {
    this._currentStep = step
    if (step === 1) {
      this.step1Target.classList.remove("hidden")
      this.step2Target.classList.add("hidden")
      this.nextBtnTarget.classList.remove("hidden")
      this.nextBtnTarget.classList.add("inline-flex")
      this.exportBtnTarget.classList.add("hidden")
      this.exportBtnTarget.classList.remove("inline-flex")
      this.backBtnTarget.classList.add("hidden")
    } else {
      this.step1Target.classList.add("hidden")
      this.step2Target.classList.remove("hidden")
      this.nextBtnTarget.classList.add("hidden")
      this.nextBtnTarget.classList.remove("inline-flex")
      this.exportBtnTarget.classList.remove("hidden")
      this.exportBtnTarget.classList.add("inline-flex")
      this.backBtnTarget.classList.remove("hidden")
    }
  }

  // ── Date Presets ───────────────────────────────────────────────────

  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    this._currentPreset = preset
    this._applyPresetDates(preset)
    this._updateDatePresetChips()
  }

  filterDatesCustom() {
    this._currentPreset = ""
    this._updateDatePresetChips()
    this._dateStart = this.dateFromTarget.value
    this._dateEnd = this.dateToTarget.value
    // Reset photos when dates change
    this._photosLoaded = false
  }

  _applyPresetDates(preset) {
    const today = new Date()
    let from = null
    let to = null

    if (preset === "today") {
      from = new Date(today); to = new Date(today)
    } else if (preset === "yesterday") {
      const d = new Date(today); d.setDate(d.getDate() - 1)
      from = d; to = new Date(d)
    } else if (preset === "7" || preset === "30") {
      const days = parseInt(preset)
      const d = new Date(today); d.setDate(d.getDate() - (days - 1))
      from = d; to = new Date(today)
    } else if (preset === "this_month") {
      from = new Date(today.getFullYear(), today.getMonth(), 1)
      to = new Date(today)
    } else if (preset === "last_month") {
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      to = new Date(today.getFullYear(), today.getMonth(), 0)
    }

    if (from && to) {
      this._dateStart = this._formatDate(from)
      this._dateEnd = this._formatDate(to)
      if (this.hasDateFromTarget) this.dateFromTarget.value = this._dateStart
      if (this.hasDateToTarget) this.dateToTarget.value = this._dateEnd
      this._photosLoaded = false
    }
  }

  _updateDatePresetChips() {
    if (!this.hasDatePresetsTarget) return
    const buttons = this.datePresetsTarget.querySelectorAll("button")
    const activeClasses = ["bg-blue-50", "text-blue-600", "font-medium", "border-blue-200"]
    const inactiveClasses = ["text-gray-500", "hover:bg-gray-50", "border-gray-200"]

    buttons.forEach((btn) => {
      const isActive = btn.dataset.preset === this._currentPreset
      btn.classList.remove(...activeClasses, ...inactiveClasses)
      btn.classList.add(...(isActive ? activeClasses : inactiveClasses))
    })
  }

  _formatDate(d) {
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
  }

  // ── Report Types ───────────────────────────────────────────────────

  addReportType() {
    if (!this.hasReportTypeSelectTarget) return
    const select = this.reportTypeSelectTarget
    const id = select.value
    if (!id || this._reportTypeIds.includes(id)) { select.value = ""; return }
    this._reportTypeIds.push(id)
    this._renderReportTypes()
    this._syncReportTypeOptions()
    select.value = ""
  }

  removeReportType(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.id
    this._reportTypeIds = this._reportTypeIds.filter(i => i !== id)
    this._renderReportTypes()
    this._syncReportTypeOptions()
  }

  _syncReportTypeOptions() {
    if (!this.hasReportTypeSelectTarget) return
    const select = this.reportTypeSelectTarget
    Array.from(select.options).forEach(opt => {
      if (!opt.value) return // skip placeholder
      opt.hidden = this._reportTypeIds.includes(opt.value)
    })
  }

  _renderReportTypes() {
    if (!this.hasReportTypesListTarget) return
    this.reportTypesListTarget.innerHTML = this._reportTypeIds.map(id => {
      const opt = this.reportTypeSelectTarget.querySelector(`option[value="${id}"]`)
      const name = opt ? opt.dataset.name : id
      return `
        <span class="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-[13px] px-2.5 py-1 rounded-full">
          ${this._escapeHtml(name)}
          <a href="#" data-action="click->progress-report#removeReportType" data-id="${id}"
             class="text-blue-400 hover:text-blue-600 cursor-pointer">
            <svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
            </svg>
          </a>
        </span>
      `
    }).join("")
  }

  // ── Comments ───────────────────────────────────────────────────────

  validateComment() {
    const hasText = this.commentInputTarget.value.trim().length > 0
    const btn = this.addCommentBtnTarget
    if (hasText) {
      btn.classList.remove("pointer-events-none", "opacity-40")
    } else {
      btn.classList.add("pointer-events-none", "opacity-40")
    }
  }

  addComment(event) {
    event.preventDefault()
    const text = this.commentInputTarget.value.trim()
    if (!text) return
    this._comments.push(text)
    this._renderComments()
    this.commentInputTarget.value = ""
    this.addCommentBtnTarget.classList.add("pointer-events-none", "opacity-40")
  }

  removeComment(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this._comments.splice(index, 1)
    this._renderComments()
  }

  _renderComments() {
    this.commentsListTarget.innerHTML = this._comments.map((c, i) => `
      <div class="flex items-start gap-2 bg-gray-50 rounded-lg px-3 py-2">
        <p class="flex-1 text-[13px] text-gray-700 whitespace-pre-wrap">${this._escapeHtml(c)}</p>
        <a href="#" data-action="click->progress-report#removeComment" data-index="${i}"
           class="shrink-0 text-gray-400 hover:text-red-500 cursor-pointer mt-0.5">
          <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
          </svg>
        </a>
      </div>
    `).join("")
  }

  // ── Photos ─────────────────────────────────────────────────────────

  photoFilterChanged() {
    this._photosLoaded = false
    this._loadPhotos()
  }

  async _loadPhotos() {
    this.photosLoadingTarget.classList.remove("hidden")
    this.photosGridTarget.innerHTML = ""
    this._selectedPhotoIds.clear()
    this._photosPage = 1
    this._isLastPhotoPage = false
    this._updatePhotoCount()

    if (this.hasSelectAllPhotosTarget) this.selectAllPhotosTarget.checked = false

    try {
      await this._fetchPhotosPage(1)
      this._photosLoaded = true
    } catch (e) {
      console.error("Photo load error:", e)
    } finally {
      this.photosLoadingTarget.classList.add("hidden")
    }
  }

  async loadMorePhotos() {
    this._photosPage++
    await this._fetchPhotosPage(this._photosPage)
  }

  async _fetchPhotosPage(page) {
    const url = new URL(this.photosUrlValue, window.location.origin)
    url.searchParams.set("page", page)
    url.searchParams.set("format", "json")

    // If filtering by date range
    const filterRange = this.hasPhotoFilterRangeTarget && this.photoFilterRangeTarget.checked
    if (filterRange && this._dateStart && this._dateEnd) {
      url.searchParams.set("start_date", this._dateStart)
      url.searchParams.set("end_date", this._dateEnd)
    }

    const resp = await fetch(url, { headers: { "Accept": "application/json" } })
    if (!resp.ok) throw new Error("Failed to load photos")
    const data = await resp.json()

    this._isLastPhotoPage = data.is_last_page
    if (this.hasLoadMoreContainerTarget) {
      this.loadMoreContainerTarget.classList.toggle("hidden", this._isLastPhotoPage)
    }

    data.photos.forEach(photo => {
      if (!photo.is_image || !photo.thumbnail_url) return
      const div = document.createElement("div")
      div.className = "relative cursor-pointer group"
      div.innerHTML = `
        <img src="${photo.thumbnail_url}" alt=""
             class="w-full aspect-square object-cover rounded border border-gray-200 group-hover:border-blue-400 opacity-0 transition-all duration-300"
             loading="lazy"
             data-controller="img-handler"
             data-action="load->img-handler#showLoaded" />
        <input type="checkbox" value="${photo.id}"
               data-action="change->progress-report#togglePhotoSelect"
               class="absolute top-1 left-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 pointer-events-none" />
      `
      div.addEventListener("click", () => {
        const cb = div.querySelector("input[type='checkbox']")
        cb.checked = !cb.checked
        cb.dispatchEvent(new Event("change", { bubbles: true }))
      })
      this.photosGridTarget.appendChild(div)
    })
  }

  togglePhotoSelect(event) {
    const id = event.currentTarget.value
    if (event.currentTarget.checked) {
      this._selectedPhotoIds.add(id)
    } else {
      this._selectedPhotoIds.delete(id)
    }
    this._updatePhotoCount()
  }

  toggleSelectAllPhotos() {
    const checked = this.selectAllPhotosTarget.checked
    const checkboxes = this.photosGridTarget.querySelectorAll("input[type='checkbox']")
    checkboxes.forEach(cb => {
      cb.checked = checked
      if (checked) {
        this._selectedPhotoIds.add(cb.value)
      } else {
        this._selectedPhotoIds.delete(cb.value)
      }
    })
    this._updatePhotoCount()
  }

  _updatePhotoCount() {
    if (this.hasPhotoCountTarget) {
      const count = this._selectedPhotoIds.size
      this.photoCountTarget.textContent = count > 0 ? `${count} foto(s) selecionada(s)` : ""
    }
  }

  // ── Export ─────────────────────────────────────────────────────────

  async exportPdf() {
    if (!this._dateStart || !this._dateEnd) {
      this._showToast("Selecione o período.", "error")
      return
    }

    this.exportBtnTarget.disabled = true
    this.exportSpinnerTarget.classList.remove("hidden")

    const params = new URLSearchParams()
    params.append("title", this.titleTarget.value)
    params.append("date_start", this._dateStart)
    params.append("date_end", this._dateEnd)
    if (this.hasResponsibleTarget && this.responsibleTarget.value) {
      params.append("responsible", this.responsibleTarget.value)
    }
    if (this.hasCompaniesInvolvedTarget && this.companiesInvolvedTarget.value) {
      params.append("companies_involved", this.companiesInvolvedTarget.value)
    }
    if (this.hasProgressOfWorkTarget && this.progressOfWorkTarget.checked) {
      params.append("progress_of_work", "on")
    }
    if (this.hasExpectedDoneTarget && this.expectedDoneTarget.checked) {
      params.append("expected_done", "on")
    }
    // Punchlist kinds
    if (this.hasPunchlistKindTarget) {
      this.punchlistKindTargets.forEach(cb => {
        if (cb.checked) params.append("punchlist_kinds[]", cb.value)
      })
    }
    // Report types
    if (this._reportTypeIds.length > 0) {
      params.append("report_types_list", this._reportTypeIds.join(" "))
    }
    // Comments
    this._comments.forEach(c => params.append("comments[]", c))
    // Photos
    this._selectedPhotoIds.forEach(id => params.append("photo_ids[]", id))

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json"
        },
        body: params.toString()
      })

      if (!resp.ok) {
        const text = await resp.text().catch(() => "")
        throw new Error(text || "Erro ao gerar PDF")
      }

      const data = await resp.json()
      this._close()

      const toastMsg = this.hasGeneratingToastValue && this.generatingToastValue
        ? this.generatingToastValue
        : "Generating report. Check below in a moment."
      this._showGeneratingToast(toastMsg)

      if (data.report) {
        const readyMsg = this.hasReadyToastValue && this.readyToastValue
          ? this.readyToastValue
          : "Progress Report is ready."
        document.dispatchEvent(new CustomEvent("special-report:created", {
          detail: { report: data.report, readyMessage: readyMsg }
        }))

        if (data.report.poll_url) {
          this._pollForCompletion(data.report.id, data.report.poll_url, readyMsg)
        }
      }
    } catch (e) {
      console.error("Progress report export error:", e)
      this._showToast(e.message || "Erro ao gerar relatório.", "error")
    } finally {
      this.exportBtnTarget.disabled = false
      this.exportSpinnerTarget.classList.add("hidden")
    }
  }

  // ── Polling ─────────────────────────────────────────────────────────

  _pollForCompletion(reportId, pollUrl, readyMsg) {
    const poll = async () => {
      try {
        const csrf = document.querySelector('meta[name="csrf-token"]')?.content
        const resp = await fetch(pollUrl, {
          headers: { "Accept": "application/json", "X-CSRF-Token": csrf },
          credentials: "same-origin"
        })
        const data = await resp.json()

        if (data.processing === 0) {
          document.dispatchEvent(new CustomEvent("special-report:ready", {
            detail: { reportId: String(reportId) }
          }))
          this._hideGeneratingToast()
          this._showToast(readyMsg, "notice")
          return
        }

        const timer = setTimeout(poll, 3000)
        this._pollTimers.push(timer)
      } catch (e) {
        console.error("Poll failed:", e)
        const timer = setTimeout(poll, 5000)
        this._pollTimers.push(timer)
      }
    }

    const timer = setTimeout(poll, 3000)
    this._pollTimers.push(timer)
  }

  _showGeneratingToast(message) {
    this._hideGeneratingToast()
    const el = document.createElement("div")
    el.id = "special-report-generating-toast"
    el.className = "flash-toast-info"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "0")
    el.setAttribute("role", "status")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>`
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _hideGeneratingToast() {
    const existing = document.getElementById("special-report-generating-toast")
    if (existing) existing.remove()
  }

  // ── Helpers ────────────────────────────────────────────────────────

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
